import { useState } from 'react';
import { Pencil, Trash2, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  FUND_TRANSACTION_TYPE_LABELS,
  type FundTransactionDetail,
  type ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatCurrency } from '@/utils/formatters';

import { DeleteLpTransferDialog } from './DeleteLpTransferDialog';
import { EditLpTransferDialog } from './EditLpTransferDialog';
import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

/**
 * A commitment moving from one LP to another. No cash moves through the fund,
 * so the signed agreement is the only proof and the figures moved are the
 * substance.
 */
export function LpTransferDetailsCard({
  transaction,
  fundId,
  fundName,
  reconciliation,
  onViewJournalEntries,
  onDelete,
}: {
  transaction: FundTransactionDetail;
  fundId: string;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
  onDelete: () => Promise<void>;
}) {
  const snapshot = transaction.lp_transfer;
  const money = (value: number | string | null) =>
    formatCurrency(value, { currency: transaction.currency });
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isDraft = transaction.status === 'DRAFT';

  return (
    <>
      <TransactionDetailsCard
        transaction={transaction}
        fundName={fundName}
        reconciliation={reconciliation}
        onViewJournalEntries={onViewJournalEntries}
        icon={<Users className="h-5 w-5" />}
        iconClassName="bg-indigo-100 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300"
        eyebrow={FUND_TRANSACTION_TYPE_LABELS[transaction.transaction_type]}
        actions={
          isDraft && (
            <>
              {snapshot && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-lp-transfer-button"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit draft
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsConfirmingDelete(true)}
                data-testid="delete-lp-transfer-button"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete draft
              </Button>
            </>
          )
        }
        fields={
          snapshot && (
            <>
              <Field label="From">{snapshot.transferor_name}</Field>
              <Field label="To">{snapshot.transferee_name}</Field>
              <Field label="Commitment transferred">
                {money(transaction.amount)}
              </Field>
              {snapshot.transferor_commitment_before != null && (
                <Field label={`${snapshot.transferor_name} commitment`}>
                  {money(snapshot.transferor_commitment_before)} →{' '}
                  {money(snapshot.transferor_commitment_after)}
                </Field>
              )}
              {snapshot.transferee_commitment_after != null && (
                <Field label={`${snapshot.transferee_name} commitment after`}>
                  {money(snapshot.transferee_commitment_after)}
                </Field>
              )}
              {snapshot.transferred_called_amount != null && (
                <Field label="Called moved">
                  {money(snapshot.transferred_called_amount)}
                </Field>
              )}
              {snapshot.transferred_contributed_amount != null && (
                <Field label="Paid-in moved">
                  {money(snapshot.transferred_contributed_amount)}
                </Field>
              )}
              <Field label="Unfunded moved">
                {money(snapshot.transferred_unfunded_commitment_amount)}
              </Field>
            </>
          )
        }
      />

      <DeleteLpTransferDialog
        open={isConfirmingDelete}
        onOpenChange={setIsConfirmingDelete}
        fundId={fundId}
        fundTransactionId={transaction.id}
        transferorName={snapshot?.transferor_name}
        onDelete={onDelete}
      />

      {snapshot && (
        <EditLpTransferDialog
          open={isEditing}
          onOpenChange={setIsEditing}
          fundId={fundId}
          transaction={transaction}
          snapshot={snapshot}
        />
      )}
    </>
  );
}
